import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Star, MapPin, Search, User, Bookmark, Trash2, BellRing } from "lucide-react";
import { toast } from "sonner";

const BE = process.env.REACT_APP_BACKEND_URL;

export default function FacilitySearch() {
    const [professions, setProfessions] = useState([]);
    const [f, setF] = useState({
        q: "", profession: "", city: "", min_experience: 0, min_rating: 0,
        radius_km: 0, only_verified: false,
    });
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [savedSearches, setSavedSearches] = useState([]);
    const [saveDialog, setSaveDialog] = useState(false);
    const [saveName, setSaveName] = useState("");

    const loadSaved = async () => {
        const { data } = await api.get("/saved-searches");
        setSavedSearches(data.data || []);
    };

    useEffect(() => {
        api.get("/professions").then(({ data }) => setProfessions(data.data || []));
        loadSaved();
    }, []);

    const run = async () => {
        setLoading(true); setSearched(true);
        const params = new URLSearchParams();
        if (f.q) params.append("q", f.q);
        if (f.profession && f.profession !== "any") params.append("profession", f.profession);
        if (f.city) params.append("city", f.city);
        if (f.min_experience) params.append("min_experience", f.min_experience);
        if (f.min_rating) params.append("min_rating", f.min_rating);
        if (f.radius_km) params.append("radius_km", f.radius_km);
        if (f.only_verified) params.append("only_verified", "true");
        const { data } = await api.get(`/search/professionals?${params}`);
        setResults(data.data || []);
        setLoading(false);
    };

    const saveSearch = async () => {
        if (!saveName.trim()) return;
        try {
            await api.post("/saved-searches", {
                name: saveName.trim(),
                profession: f.profession && f.profession !== "any" ? f.profession : null,
                city: f.city || null,
                min_experience: f.min_experience || null,
                min_rating: f.min_rating || null,
                only_verified: f.only_verified,
            });
            toast.success("Ricerca salvata. Ti notificheremo quando un nuovo professionista corrisponde.");
            setSaveName(""); setSaveDialog(false); loadSaved();
        } catch { toast.error("Errore"); }
    };

    const applySaved = (s) => {
        setF({
            q: "", profession: s.profession || "", city: s.city || "",
            min_experience: s.min_experience || 0, min_rating: s.min_rating || 0,
            radius_km: 0, only_verified: !!s.only_verified,
        });
        setTimeout(run, 100);
    };

    const deleteSaved = async (id) => {
        await api.delete(`/saved-searches/${id}`);
        loadSaved();
    };

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Ricerca professionisti</h1>
                    <p className="text-sm text-[#5C5C5C] mt-1">Trova il professionista giusto con filtri avanzati.</p>
                </div>
                {searched && results.length > 0 && (
                    <Button variant="outline" onClick={() => setSaveDialog(true)} data-testid="save-search-btn">
                        <Bookmark className="w-4 h-4 mr-2" />Salva ricerca
                    </Button>
                )}
            </div>

            {savedSearches.length > 0 && (
                <div className="mb-6 hl-card p-4" data-testid="saved-searches-list">
                    <div className="flex items-center gap-2 mb-3">
                        <BellRing className="w-4 h-4 text-[#1C3F39]" />
                        <h2 className="font-display font-bold text-sm">Ricerche salvate</h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {savedSearches.map((s) => (
                            <div key={s.id} className="flex items-center gap-1 pl-3 pr-1 py-1 border border-[#E5E5E0] rounded-full bg-[#F9F9F7]"
                                data-testid={`saved-${s.id}`}>
                                <button onClick={() => applySaved(s)} className="text-sm font-medium">{s.name}</button>
                                <button onClick={() => deleteSaved(s.id)} className="w-6 h-6 rounded-full hover:bg-white transition-colors grid place-items-center" data-testid={`del-saved-${s.id}`}>
                                    <Trash2 className="w-3 h-3 text-[#5C5C5C]" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="hl-card p-6 mb-6 grid md:grid-cols-3 gap-4" data-testid="search-filters">
                <div className="md:col-span-3">
                    <Label>Ricerca testuale</Label>
                    <div className="relative mt-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                        <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && run()}
                            placeholder="Cerca in nome, bio, competenze, specializzazioni…"
                            className="pl-9" data-testid="filter-text" />
                    </div>
                </div>
                <div>
                    <Label>Professione</Label>
                    <Select value={f.profession || "any"} onValueChange={(v) => setF({ ...f, profession: v === "any" ? "" : v })}>
                        <SelectTrigger data-testid="filter-profession"><SelectValue placeholder="Tutte" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Tutte</SelectItem>
                            {professions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Città</Label>
                    <Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })}
                        placeholder="es. Milano" data-testid="filter-city" />
                </div>
                <div>
                    <Label>Esperienza minima (anni)</Label>
                    <Input type="number" min={0} value={f.min_experience}
                        onChange={(e) => setF({ ...f, min_experience: parseInt(e.target.value || 0) })}
                        data-testid="filter-experience" />
                </div>
                <div>
                    <Label>Rating minimo (0-5)</Label>
                    <Input type="number" step="0.5" min={0} max={5} value={f.min_rating}
                        onChange={(e) => setF({ ...f, min_rating: parseFloat(e.target.value || 0) })}
                        data-testid="filter-rating" />
                </div>
                <div>
                    <Label>Raggio (km, richiede coordinate)</Label>
                    <Input type="number" min={0} value={f.radius_km}
                        onChange={(e) => setF({ ...f, radius_km: parseInt(e.target.value || 0) })}
                        data-testid="filter-radius" />
                </div>
                <div className="flex items-end gap-3">
                    <Switch checked={f.only_verified} onCheckedChange={(v) => setF({ ...f, only_verified: v })}
                        id="verified-only" data-testid="filter-verified" />
                    <Label htmlFor="verified-only">Solo verificati</Label>
                </div>
                <div className="md:col-span-3 flex justify-end">
                    <Button onClick={run} disabled={loading} className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="search-btn">
                        <Search className="w-4 h-4 mr-2" />{loading ? "Ricerca…" : "Cerca"}
                    </Button>
                </div>
            </div>

            {searched && (
                <div className="mb-4 text-sm text-[#5C5C5C]" data-testid="results-count">
                    {results.length} risultati
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
                {results.map((p) => (
                    <div key={p.id} className="hl-card p-5 flex gap-4" data-testid={`pro-result-${p.id}`}>
                        <div className="w-14 h-14 rounded-full bg-[#EEF3F1] overflow-hidden grid place-items-center flex-shrink-0">
                            {p.photo_url
                                ? <img src={`${BE}${p.photo_url}`} alt="" className="w-full h-full object-cover" />
                                : <User className="w-6 h-6 text-[#1C3F39]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <div className="font-display font-bold truncate">{p.full_name}</div>
                                {p.verification_status === "VERIFIED" && (
                                    <ShieldCheck className="w-4 h-4 text-emerald-600" data-testid={`verified-${p.id}`} />
                                )}
                            </div>
                            <div className="text-sm text-[#5C5C5C]">{p.profession || "—"}</div>
                            <div className="mt-2 flex items-center gap-3 text-xs text-[#5C5C5C]">
                                {p.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}{p.distance_km != null && ` · ${p.distance_km} km`}</span>}
                                {p.years_experience != null && <span>{p.years_experience} anni</span>}
                                {p.rating != null && (
                                    <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-[#D96C5B] text-[#D96C5B]" />{p.rating} ({p.reviews_count})</span>
                                )}
                            </div>
                            {p.bio && <p className="text-xs text-[#5C5C5C] mt-2 line-clamp-2">{p.bio}</p>}
                        </div>
                    </div>
                ))}
                {searched && !loading && results.length === 0 && (
                    <div className="hl-card p-10 text-center col-span-full text-[#5C5C5C]">Nessun risultato con questi filtri.</div>
                )}
            </div>

            <Dialog open={saveDialog} onOpenChange={setSaveDialog}>
                <DialogContent data-testid="save-search-dialog">
                    <DialogHeader><DialogTitle>Salva questa ricerca</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-[#5C5C5C]">Ricevi una notifica ed email quando un nuovo professionista corrisponde ai tuoi filtri.</p>
                        <Label>Nome ricerca</Label>
                        <Input value={saveName} onChange={(e) => setSaveName(e.target.value)}
                            placeholder="es. Igienisti Milano" data-testid="save-search-name-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSaveDialog(false)}>Annulla</Button>
                        <Button onClick={saveSearch} disabled={!saveName.trim()}
                            className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="save-search-confirm-btn">Salva</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
